import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import { Controller, Path, useForm } from "react-hook-form";
import {
    appSectionCardStyle,
    renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";
import { useNurseFormAutoSave } from "../useNurseFormAutoSave";

export interface Step06FormValues {
    residentOccupation: string;
    residentEducation: string;
    residentHobbies: string;
    involvementPrefersAlone: boolean;
    involvementAtEaseWithOthers: boolean;
    involvementSelfInitiates: boolean;
    involvementEnjoysGroup: boolean;
    involvementEnjoysNew: boolean;
    involvementLimitations: string;
    involvementComments: string;
    familyCloseRelationships: string;
    familySomeoneToConfideIn: string;
    recentLossOfFamilyFriend: boolean;
    familyComfortStrategies: string;
    familyComments: string;
    socialCulturalConsiderations: string;
    socialEnjoysChildren: boolean;
    socialEnjoysPets: boolean;
    socialHasPet: boolean;
    socialComments: string;
    sleepStaysUpLate: boolean;
    sleepArisesEarly: boolean;
    sleepSleepsIn: boolean;
    sleepNaps: boolean;
    sleepIrregular: boolean;
    sleepAwakesAtNight: boolean;
    sleepComments: string;
    faithFindsStrength: boolean;
    faithAttendsActivities: string;
    faithComments: string;
    householdHelpsWithLaundry: boolean;
    householdHelpsWithCooking: boolean;
    householdHelpsWithOther: string;
    householdComments: string;
    activityTimeMorning: boolean;
    activityTimeAfternoon: boolean;
    activityTimeEvening: boolean;
    activityTimeNight: boolean;
    activityTimeComments: string;
    activityMusic: boolean;
    activityCardsGames: boolean;
    activityTrips: boolean;
    activityGardening: boolean;
    activityOutdoors: boolean;
    activityTalking: boolean;
    activityHelpingOthers: boolean;
    activityComputers: boolean;
    activityReadingWriting: boolean;
    activityTV: boolean;
    activityExercise: boolean;
    activityCrafts: boolean;
    activityOther: string;
    activityComments: string;
}

export const DEFAULT_STEP06: Step06FormValues = {
    residentOccupation: "", residentEducation: "", residentHobbies: "",
    involvementPrefersAlone: false, involvementAtEaseWithOthers: false, involvementSelfInitiates: false,
    involvementEnjoysGroup: false, involvementEnjoysNew: false, involvementLimitations: "", involvementComments: "",
    familyCloseRelationships: "", familySomeoneToConfideIn: "", recentLossOfFamilyFriend: false, familyComfortStrategies: "", familyComments: "",
    socialCulturalConsiderations: "", socialEnjoysChildren: false, socialEnjoysPets: false, socialHasPet: false, socialComments: "",
    sleepStaysUpLate: false, sleepArisesEarly: false, sleepSleepsIn: false, sleepNaps: false, sleepIrregular: false, sleepAwakesAtNight: false, sleepComments: "",
    faithFindsStrength: false, faithAttendsActivities: "", faithComments: "",
    householdHelpsWithLaundry: false, householdHelpsWithCooking: false, householdHelpsWithOther: "", householdComments: "",
    activityTimeMorning: false, activityTimeAfternoon: false, activityTimeEvening: false, activityTimeNight: false, activityTimeComments: "",
    activityMusic: false, activityCardsGames: false, activityTrips: false, activityGardening: false, activityOutdoors: false,
    activityTalking: false, activityHelpingOthers: false, activityComputers: false, activityReadingWriting: false,
    activityTV: false, activityExercise: false, activityCrafts: false, activityOther: "", activityComments: "",
};

export interface Step06Props {
    sectionData: Partial<Step06FormValues> | null;
    residentId: string;
    formId: string;
    onSave: (data: Step06FormValues) => Promise<void>;
    onNext: () => void;
    onBack: () => void;
    loading?: boolean;
}

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="col-12 md:col-4 mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: "#6b7280" }}>{label}</div>
        <div className="text-sm font-medium" style={{ color: value ? "#111827" : "#9ca3af" }}>{value || "—"}</div>
    </div>
);

const formatSelected = (items: Array<string | false | null | undefined>) => {
    const selected = items.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    return selected.length ? selected.join(", ") : undefined;
};

const Step06PsychosocialProfile: React.FC<Step06Props> = ({
    sectionData, formId, onSave, onNext, onBack, loading,
}) => {
    const toast = useRef<Toast>(null);
    const [enableEditing, setEnableEditing] = useState<boolean>(false);
    const defaultValues: Step06FormValues = { ...DEFAULT_STEP06, ...(sectionData ?? {}) };
    const { control, handleSubmit, watch, reset } = useForm<Step06FormValues>({ defaultValues });

    const onSubmit = async (data: Step06FormValues) => { await onSave(data); };
    const handleSave = async (data: Step06FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Psychosocial profile saved.", life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    const handleSaveAndContinue = async (data: Step06FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Saved. Moving to next step.", life: 2000 });
            onNext();
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    useNurseFormAutoSave({ enabled: enableEditing && !!formId, baselineData: defaultValues, watch, handleSubmit, onSubmit });

    const values = watch();

    const cb = (name: Path<Step06FormValues>, label: string) => (
        <Controller key={name} name={name} control={control} render={({ field: f }) => (
            <div className="flex align-items-center gap-2 mb-2">
                <Checkbox inputId={name} checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                <label htmlFor={name} className="text-sm cursor-pointer">{label}</label>
            </div>
        )} />
    );
    const txt = (name: Path<Step06FormValues>, placeholder: string, label?: string) => (
        <Controller key={name} name={name} control={control} render={({ field: f }) => (
            <div>
                {label && <label className="font-medium text-sm block mb-1">{label}</label>}
                <InputText {...(f as any)} className="w-full" placeholder={placeholder} />
            </div>
        )} />
    );
    const area = (name: Path<Step06FormValues>, placeholder: string, label?: string) => (
        <Controller key={name} name={name} control={control} render={({ field: f }) => (
            <div>
                {label && <label className="font-medium text-sm block mb-1">{label}</label>}
                <InputTextarea {...(f as any)} className="w-full" rows={2} autoResize placeholder={placeholder} />
            </div>
        )} />
    );

    return (
        <>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit(handleSave)}>
                <div className="flex justify-content-center gap-2 mb-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
                {!enableEditing ? (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Psychosocial Background", "Occupation, education and lifetime hobbies.", "pi pi-user")}
                            <div className="p-3 grid">
                                <InfoRow label="Occupation" value={values.residentOccupation} />
                                <InfoRow label="Education" value={values.residentEducation} />
                                <InfoRow label="Lifetime Hobbies" value={values.residentHobbies} />
                            </div>
                        </Card>

                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Involvement Patterns", "Social engagement preferences and limitations.", "pi pi-users")}
                            <div className="p-3 grid">
                                <InfoRow label="Prefers to be alone" value={values.involvementPrefersAlone ? "Yes" : "No"} />
                                <InfoRow label="At ease with others" value={values.involvementAtEaseWithOthers ? "Yes" : "No"} />
                                <InfoRow label="Self-initiates activities" value={values.involvementSelfInitiates ? "Yes" : "No"} />
                                <InfoRow label="Enjoys group activities" value={values.involvementEnjoysGroup ? "Yes" : "No"} />
                                <InfoRow label="Enjoys new activities" value={values.involvementEnjoysNew ? "Yes" : "No"} />
                                <InfoRow label="Limitations" value={values.involvementLimitations} />
                                <InfoRow label="Comments" value={values.involvementComments} />
                            </div>
                        </Card>

                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Family/Friends Relationship", "Support network and close relationships.", "pi pi-heart")}
                            <div className="p-3 grid">
                                <InfoRow label="Close relationships" value={values.familyCloseRelationships} />
                                <InfoRow label="Someone to confide in" value={values.familySomeoneToConfideIn} />
                                <InfoRow label="Recent loss of family or friend" value={values.recentLossOfFamilyFriend ? "Yes" : "No"} />
                                <InfoRow label="Comfort strategies/items" value={values.familyComfortStrategies} />
                                <InfoRow label="Comments" value={values.familyComments} />
                            </div>
                        </Card>

                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Social/Cultural Preferences", "Cultural considerations and preferences.", "pi pi-globe")}
                            <div className="p-3 grid">
                                <InfoRow label="Cultural considerations" value={values.socialCulturalConsiderations} />
                                <InfoRow label="Enjoys children" value={values.socialEnjoysChildren ? "Yes" : "No"} />
                                <InfoRow label="Enjoys pets" value={values.socialEnjoysPets ? "Yes" : "No"} />
                                <InfoRow label="Has pet they want to keep" value={values.socialHasPet ? "Yes" : "No"} />
                                <InfoRow label="Comments" value={values.socialComments} />
                            </div>
                        </Card>

                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Usual Sleep Patterns", "Sleep and rest habits.", "pi pi-moon")}
                            <div className="p-3 grid">
                                <InfoRow label="Selected patterns" value={formatSelected([
                                    values.sleepStaysUpLate && "Stays up late",
                                    values.sleepArisesEarly && "Arises early",
                                    values.sleepSleepsIn && "Sleeps in",
                                    values.sleepNaps && "Naps",
                                    values.sleepIrregular && "Irregular sleep",
                                    values.sleepAwakesAtNight && "Awakes at night",
                                ])} />
                                <InfoRow label="Comments" value={values.sleepComments} />
                            </div>
                        </Card>

                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Spiritual/Faith Preferences", "Faith and spiritual activities.", "pi pi-star")}
                            <div className="p-3 grid">
                                <InfoRow label="Finds strength in faith" value={values.faithFindsStrength ? "Yes" : "No"} />
                                <InfoRow label="Attends spiritual activities" value={values.faithAttendsActivities} />
                                <InfoRow label="Comments" value={values.faithComments} />
                            </div>
                        </Card>

                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Preferred Household Activities", "Household tasks the resident enjoys helping with.", "pi pi-home")}
                            <div className="p-3 grid">
                                <InfoRow label="Laundry" value={values.householdHelpsWithLaundry ? "Yes" : "No"} />
                                <InfoRow label="Cooking" value={values.householdHelpsWithCooking ? "Yes" : "No"} />
                                <InfoRow label="Other activities" value={values.householdHelpsWithOther} />
                                <InfoRow label="Comments" value={values.householdComments} />
                            </div>
                        </Card>

                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Activity Preferences", "Preferred times and types of activities.", "pi pi-calendar")}
                            <div className="p-3 grid">
                                <InfoRow label="Preferred times" value={formatSelected([
                                    values.activityTimeMorning && "Morning",
                                    values.activityTimeAfternoon && "Afternoon",
                                    values.activityTimeEvening && "Evening",
                                    values.activityTimeNight && "Night",
                                ])} />
                                <InfoRow label="Time comments" value={values.activityTimeComments} />
                                <InfoRow label="Activity interests" value={formatSelected([
                                    values.activityMusic && "Music",
                                    values.activityCardsGames && "Cards/Games",
                                    values.activityTrips && "Trips/Shopping",
                                    values.activityGardening && "Gardening/Plants",
                                    values.activityOutdoors && "Time Outdoors",
                                    values.activityTalking && "Talking/Conversing",
                                    values.activityHelpingOthers && "Helping Others",
                                    values.activityComputers && "Computers",
                                    values.activityReadingWriting && "Reading/Writing",
                                    values.activityTV && "TV",
                                    values.activityExercise && "Exercise/Sports",
                                    values.activityCrafts && "Crafts/Arts",
                                ])} />
                                <InfoRow label="Other activity interests" value={values.activityOther} />
                                <InfoRow label="Comments" value={values.activityComments} />
                            </div>
                        </Card>
                    </>
                ) : (
                    <>
                        {/* Background */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Psychosocial Background", "Occupation, education and lifetime hobbies.", "pi pi-user")}
                            <div className="p-3 grid">
                                <div className="col-12 md:col-4">{txt("residentOccupation", "Occupation", "Current or Prior Occupation")}</div>
                                <div className="col-12 md:col-4">{txt("residentEducation", "Education level", "Education")}</div>
                                <div className="col-12 md:col-4">{txt("residentHobbies", "Lifetime hobbies", "Lifetime Hobbies")}</div>
                            </div>
                        </Card>

                        {/* Involvement Patterns */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Involvement Patterns", "Social engagement preferences and limitations.", "pi pi-users")}
                            <div className="p-3">
                                <div className="grid mb-2">
                                    <div className="col-12 md:col-6">
                                        {cb("involvementPrefersAlone", "Prefers to be alone")}
                                        {cb("involvementAtEaseWithOthers", "At ease with others")}
                                        {cb("involvementSelfInitiates", "Self-initiates activities")}
                                    </div>
                                    <div className="col-12 md:col-6">
                                        {cb("involvementEnjoysGroup", "Enjoys group activities")}
                                        {cb("involvementEnjoysNew", "Enjoys new activities")}
                                    </div>
                                </div>
                                {txt("involvementLimitations", "Describe limitations", "Limitations that impact involvement")}
                                <div className="mt-2">{area("involvementComments", "Comments", "Comments")}</div>
                            </div>
                        </Card>

                        {/* Family/Friends */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Family/Friends Relationship", "Support network and close relationships.", "pi pi-heart")}
                            <div className="p-3 grid">
                                <div className="col-12 md:col-6">{txt("familyCloseRelationships", "Name/relationship", "Close relationships")}</div>
                                <div className="col-12 md:col-6">{txt("familySomeoneToConfideIn", "Name/relationship", "Someone to confide in")}</div>
                                <div className="col-12">{cb("recentLossOfFamilyFriend", "Recent loss of family member or friend")}</div>
                                <div className="col-12">{txt("familyComfortStrategies", "Strategies or comfort items", "Strategies/items to increase comfort")}</div>
                                <div className="col-12">{area("familyComments", "Comments", "Comments")}</div>
                            </div>
                        </Card>

                        {/* Social/Cultural */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Social/Cultural Preferences", "Cultural considerations and preferences.", "pi pi-globe")}
                            <div className="p-3">
                                <div className="mb-2">{txt("socialCulturalConsiderations", "Cultural preferences", "Cultural considerations or preferences")}</div>
                                <div className="grid mb-2">
                                    <div className="col-12 md:col-4">{cb("socialEnjoysChildren", "Enjoys children")}</div>
                                    <div className="col-12 md:col-4">{cb("socialEnjoysPets", "Enjoys pets")}</div>
                                    <div className="col-12 md:col-4">{cb("socialHasPet", "Has a pet they want to keep")}</div>
                                </div>
                                {area("socialComments", "Comments", "Comments")}
                            </div>
                        </Card>

                        {/* Usual Sleep Patterns */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Usual Sleep Patterns", "Sleep and rest habits.", "pi pi-moon")}
                            <div className="p-3">
                                <div className="grid mb-2">
                                    <div className="col-6 md:col-4">{cb("sleepStaysUpLate", "Stays up late")}</div>
                                    <div className="col-6 md:col-4">{cb("sleepArisesEarly", "Arises early")}</div>
                                    <div className="col-6 md:col-4">{cb("sleepSleepsIn", "Sleeps in")}</div>
                                    <div className="col-6 md:col-4">{cb("sleepNaps", "Naps")}</div>
                                    <div className="col-6 md:col-4">{cb("sleepIrregular", "Irregular sleep")}</div>
                                    <div className="col-6 md:col-4">{cb("sleepAwakesAtNight", "Awakes at night")}</div>
                                </div>
                                {area("sleepComments", "Comments", "Comments")}
                            </div>
                        </Card>

                        {/* Faith */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Spiritual/Faith Preferences", "Faith and spiritual activities.", "pi pi-star")}
                            <div className="p-3">
                                <div className="mb-2">{cb("faithFindsStrength", "Finds strength in faith")}</div>
                                <div className="mb-2">{txt("faithAttendsActivities", "Describe attendance", "Attends church/spiritual activities")}</div>
                                {area("faithComments", "Comments", "Comments")}
                            </div>
                        </Card>

                        {/* Household Activities */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Preferred Household Activities", "Household tasks the resident enjoys helping with.", "pi pi-home")}
                            <div className="p-3">
                                <div className="grid mb-2">
                                    <div className="col-6 md:col-3">{cb("householdHelpsWithLaundry", "Laundry")}</div>
                                    <div className="col-6 md:col-3">{cb("householdHelpsWithCooking", "Cooking")}</div>
                                </div>
                                <div className="mb-2">{txt("householdHelpsWithOther", "Other household activities", "Other")}</div>
                                {area("householdComments", "Comments", "Comments")}
                            </div>
                        </Card>

                        {/* Activity Time & Preferences */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Activity Preferences", "Preferred times and types of activities.", "pi pi-calendar")}
                            <div className="p-3">
                                <p className="font-semibold text-sm mb-2">Preferred Activity Time</p>
                                <div className="grid mb-2">
                                    <div className="col-6 md:col-3">{cb("activityTimeMorning", "Morning")}</div>
                                    <div className="col-6 md:col-3">{cb("activityTimeAfternoon", "Afternoon")}</div>
                                    <div className="col-6 md:col-3">{cb("activityTimeEvening", "Evening")}</div>
                                    <div className="col-6 md:col-3">{cb("activityTimeNight", "Night")}</div>
                                </div>
                                <div className="mb-3">{area("activityTimeComments", "Comments on timing", "Time Comments")}</div>
                                <p className="font-semibold text-sm mb-2">Activity Interests</p>
                                <div className="grid mb-2">
                                    <div className="col-6 md:col-3">{cb("activityMusic", "Music")}</div>
                                    <div className="col-6 md:col-3">{cb("activityCardsGames", "Cards/Games")}</div>
                                    <div className="col-6 md:col-3">{cb("activityTrips", "Trips/Shopping")}</div>
                                    <div className="col-6 md:col-3">{cb("activityGardening", "Gardening/Plants")}</div>
                                    <div className="col-6 md:col-3">{cb("activityOutdoors", "Time Outdoors")}</div>
                                    <div className="col-6 md:col-3">{cb("activityTalking", "Talking/Conversing")}</div>
                                    <div className="col-6 md:col-3">{cb("activityHelpingOthers", "Helping Others")}</div>
                                    <div className="col-6 md:col-3">{cb("activityComputers", "Computers")}</div>
                                    <div className="col-6 md:col-3">{cb("activityReadingWriting", "Reading/Writing")}</div>
                                    <div className="col-6 md:col-3">{cb("activityTV", "TV")}</div>
                                    <div className="col-6 md:col-3">{cb("activityExercise", "Exercise/Sports")}</div>
                                    <div className="col-6 md:col-3">{cb("activityCrafts", "Crafts/Arts")}</div>
                                </div>
                                <div className="mb-2">{txt("activityOther", "Other interests", "Other Activity Interests")}</div>
                                {area("activityComments", "Comments", "Comments")}
                            </div>
                        </Card>

                    </>
                )}
                <div className="flex justify-content-center gap-2 mt-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
            </form>
        </>
    );
};

export default Step06PsychosocialProfile;
